/**
 * Camlin Companion — bridge page-context extractor (doc 116 §2.1.2, CO2).
 *
 * "Live page context (what the visitor sees right now): the bridge extracts
 * the current URL, title, headings, and a bounded visible-text digest, and
 * sends it with each turn (and on SPA navigation)."
 *
 * Split into two layers, following `chatTranscript.ts`'s convention:
 *
 *   - `extractPageContext` — pure, duck-typed against `PageContextSource`
 *     (NOT a real DOM `Document`), so it is fully unit-testable under this
 *     project's `tsx --test` runner without jsdom.
 *   - `watchPageContext` — the real DOM wiring (history monkey-patch,
 *     popstate, debounce) that calls `readPageContextSourceFromDocument`
 *     and `extractPageContext` on every SPA navigation. Untestable under
 *     `tsx --test` (no `window`); covered by source-string assertions in
 *     `pageContext.test.ts`, matching `useLiveListenControls.ts`'s
 *     established convention for this project.
 *
 * Privacy/safety posture (doc 116 §6): this digest is later rendered
 * server-side (`agentLoop.ts`'s `formatPageContextForModel`) as clearly
 * labeled UNTRUSTED data, never as an instruction — nothing here needs to
 * sanitize page text against injection, only to bound its size.
 */

import type { CompanionPageContext } from './types';

/** Headings budget — generous but bounded; the model-facing render applies its own (smaller) cap independently. */
export const DEFAULT_PAGE_CONTEXT_MAX_HEADINGS = 20;
/** Visible-text digest budget in characters, pre-truncation. */
export const DEFAULT_PAGE_CONTEXT_MAX_DIGEST_CHARS = 4000;
/** SPA navigations are coalesced within this window so a burst of pushState calls (common in router libraries) emits once. */
export const PAGE_CONTEXT_NAVIGATION_DEBOUNCE_MS = 250;

/** Minimal duck-typed surface `extractPageContext` needs — a real `Document` satisfies the shape callers build in `readPageContextSourceFromDocument`. */
export interface PageContextSource {
  url: string;
  title: string;
  headingTexts: string[];
  visibleText: string;
}

export function truncateVisibleText(text: string, maxChars: number): string {
  if (text.length <= maxChars) {
    return text;
  }
  if (maxChars <= 0) {
    return '';
  }
  return `${text.slice(0, maxChars - 1)}…`;
}

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Pure extraction: given a `PageContextSource` (already-read strings — no
 * DOM traversal here), produce a bounded `CompanionPageContext`.
 */
export function extractPageContext(
  source: PageContextSource,
  now: () => string = () => new Date().toISOString()
): CompanionPageContext {
  const headings = source.headingTexts
    .map((heading) => heading.trim())
    .filter((heading) => heading.length > 0)
    .slice(0, DEFAULT_PAGE_CONTEXT_MAX_HEADINGS);

  const visibleTextDigest = truncateVisibleText(
    collapseWhitespace(source.visibleText),
    DEFAULT_PAGE_CONTEXT_MAX_DIGEST_CHARS
  );

  return {
    url: source.url,
    title: source.title,
    headings,
    visibleTextDigest,
    capturedAt: now()
  };
}

/** True when `nextUrl` differs from the last-captured context's URL (or there is no previous capture yet). */
export function hasPageContextNavigated(
  previous: CompanionPageContext | null,
  nextUrl: string
): boolean {
  return previous === null || previous.url !== nextUrl;
}

const HEADING_SELECTOR = 'h1, h2, h3';

/**
 * Reads a `PageContextSource` from the live DOM. `document.body.innerText`
 * (not `textContent`) is used deliberately — `innerText` is layout-aware and
 * already excludes `display:none`/`visibility:hidden` content, which is the
 * closest built-in approximation of "what the visitor sees right now".
 */
export function readPageContextSourceFromDocument(doc: Document = document): PageContextSource {
  const headingTexts = Array.from(doc.querySelectorAll(HEADING_SELECTOR)).map(
    (el) => el.textContent || ''
  );
  const visibleText = doc.body ? doc.body.innerText || doc.body.textContent || '' : '';

  return {
    url: doc.location.href,
    title: doc.title,
    headingTexts,
    visibleText
  };
}

export interface PageContextWatcherHandle {
  stop: () => void;
  /** Force an immediate re-capture and emit (bypassing the debounce/URL-change check) — used for the initial capture. */
  captureNow: () => void;
}

export interface WatchPageContextOptions {
  onChange: (context: CompanionPageContext) => void;
  doc?: Document;
  win?: Window;
  debounceMs?: number;
}

/**
 * Wires SPA-navigation-aware page-context capture: an initial capture, then
 * a re-capture on every `popstate` and every `history.pushState`/
 * `replaceState` call (the two ways a client-side router changes the URL
 * without a full page load), debounced so a burst of router-internal calls
 * emits once. Restores the original `history` methods on `stop()`.
 */
export function watchPageContext(options: WatchPageContextOptions): PageContextWatcherHandle {
  const doc = options.doc ?? document;
  const win = options.win ?? window;
  const debounceMs = options.debounceMs ?? PAGE_CONTEXT_NAVIGATION_DEBOUNCE_MS;

  let lastEmitted: CompanionPageContext | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const captureAndMaybeEmit = () => {
    const nextUrl = doc.location.href;
    if (!hasPageContextNavigated(lastEmitted, nextUrl)) {
      return;
    }
    const context = extractPageContext(readPageContextSourceFromDocument(doc));
    lastEmitted = context;
    options.onChange(context);
  };

  const scheduleCapture = () => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(captureAndMaybeEmit, debounceMs);
  };

  const originalPushState = win.history.pushState.bind(win.history);
  const originalReplaceState = win.history.replaceState.bind(win.history);

  win.history.pushState = function patchedPushState(...args: Parameters<History['pushState']>) {
    const result = originalPushState(...args);
    scheduleCapture();
    return result;
  };
  win.history.replaceState = function patchedReplaceState(...args: Parameters<History['replaceState']>) {
    const result = originalReplaceState(...args);
    scheduleCapture();
    return result;
  };

  win.addEventListener('popstate', scheduleCapture);

  function stop(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    win.removeEventListener('popstate', scheduleCapture);
    win.history.pushState = originalPushState;
    win.history.replaceState = originalReplaceState;
  }

  // Initial capture runs immediately (not debounced) — the first turn
  // should never wait 250ms for a page-context digest.
  captureAndMaybeEmit();

  return { stop, captureNow: captureAndMaybeEmit };
}
